import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Upload, Download, Plus, Loader, Trash2, RotateCcw, AlertTriangle, Edit, X, Image, Hand } from 'lucide-react';
import JSZip from 'jszip';
import { logoService } from './logoService';
import { addSampleLogos } from './logoSampleData';
import { UploadLogoModal } from './UploadLogoModal';
import { Logo } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { DocumentSnapshot } from 'firebase/firestore';

// Enhanced Tag Chip Input Component (same as People module)
interface TagChipInputProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  existingTags: string[];
  placeholder?: string;
}

const TagChipInput: React.FC<TagChipInputProps> = ({ tags, onTagsChange, existingTags, placeholder }) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputValue.trim().length > 0) {
      const filtered = existingTags
        .filter(tag => 
          tag.toLowerCase().includes(inputValue.toLowerCase()) &&
          !tags.includes(tag)
        )
        .slice(0, 8);
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  }, [inputValue, tags, existingTags]);

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      onTagsChange([...tags, trimmedTag]);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const removeTag = (tagToRemove: string) => {
    onTagsChange(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showSuggestions && filteredSuggestions.length > 0) {
        addTag(filteredSuggestions[0]);
      } else if (inputValue.trim()) {
        addTag(inputValue);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    addTag(suggestion);
    inputRef.current?.focus();
  };

  const isExistingTag = (tag: string) => {
    return existingTags.includes(tag);
  };

  return (
    <div>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (inputValue.trim().length > 0) setShowSuggestions(true);
          }}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          className="w-full p-3 border rounded-lg"
          placeholder={placeholder || "Add tags..."}
        />
        
        {/* Add Button */}
        {inputValue.trim() && (
          <button
            type="button"
            onClick={() => addTag(inputValue)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-primary transition-colors"
          >
            <Plus size={20} />
          </button>
        )}
        
        {/* Suggestions Dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 z-10 max-h-48 overflow-y-auto">
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center justify-between text-sm"
              >
                <span>{suggestion}</span>
                {index === 0 && (
                  <span className="text-xs text-gray-400">Press Enter</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tag Chips */}
      {tags.length > 0 && (
        <div className="mt-3">
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                  isExistingTag(tag)
                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                    : 'bg-green-100 text-green-800 border border-green-200'
                }`}
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="hover:text-red-600 transition-colors"
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              Existing tags
            </span>
            <span className="ml-4 inline-flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              New tags
            </span>
          </p>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-1">
        Type to see suggestions, press Enter to add new tags, or click suggestions to add existing ones.
      </p>
    </div>
  );
};

// Edit Logo Modal Component
interface EditLogoModalProps {
  isOpen: boolean;
  logo: Logo | null;
  onClose: () => void;
  onSuccess: () => void;
  existingTags: string[];
}

const EditLogoModal: React.FC<EditLogoModalProps> = ({ isOpen, logo, onClose, onSuccess, existingTags }) => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    project: '',
    tags: [] as string[],
    notes: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (logo) {
      setFormData({
        name: logo.name,
        project: logo.project,
        tags: [...logo.tags],
        notes: logo.notes
      });
    }
  }, [logo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!logo || !user) return;

    if (!formData.name.trim()) {
      setError('Logo name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await logoService.updateLogo(logo.id, {
        name: formData.name.trim(),
        project: formData.project.trim() || 'Untitled Project',
        tags: formData.tags,
        notes: formData.notes.trim(),
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      setError(error.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    setError('');
    onClose();
  };

  if (!isOpen || !logo) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Edit Logo: {logo.name}</h2>
            <button
              onClick={handleClose}
              disabled={saving}
              className="text-muted-foreground hover:text-foreground"
            >
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">Current Logo</label>
              <div className="border rounded-lg p-4 bg-muted flex items-center justify-center h-32">
                <img 
                  src={logo.fileUrl} 
                  alt={logo.name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Logo file cannot be changed. To replace the logo, upload a new one and delete this one.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Logo Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Enter logo name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Project</label>
                <input
                  type="text"
                  value={formData.project}
                  onChange={(e) => setFormData(prev => ({ ...prev, project: e.target.value }))}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Original project name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Tags</label>
              <TagChipInput
                tags={formData.tags}
                onTagsChange={(tags) => setFormData(prev => ({ ...prev, tags }))}
                existingTags={existingTags}
                placeholder="Add tags (e.g., tech, blue, modern)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full p-3 border rounded-lg h-24"
                placeholder="Additional notes or description"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-accent disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!formData.name.trim() || saving}
                className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg border border-primary flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-primary/90"
              >
                {saving ? (
                  <>
                    <Loader className="animate-spin" size={16} />
                    Updating...
                  </>
                ) : (
                  'Update Logo'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export const LogosModule: React.FC = () => {
  const { user, permissions, canClaimContent, canDeleteContent } = useAuth();
  const [logos, setLogos] = useState<Logo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState<'available' | 'claimed' | 'deleted'>('available');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLogo, setEditingLogo] = useState<Logo | null>(null);
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [tabCounts, setTabCounts] = useState({ available: 0, claimed: 0, deleted: 0 });
  const [showAllTags, setShowAllTags] = useState(false);
  
  // Pagination state
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | undefined>();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load initial data
  useEffect(() => {
    loadLogos(true);
    loadTabCounts();
    loadAllTags();
  }, [activeTab, debouncedSearchTerm, activeFilters]);

  // Get all tags with counts (similar to people module)
  const getAllTagsWithCounts = () => {
    const tagCounts = new Map<string, number>();
    
    // Filter logos based on current tab
    const relevantLogos = logos.filter(logo => {
      if (activeTab === 'available') return !logo.claimed && !logo.deleted;
      if (activeTab === 'claimed') return logo.claimed && !logo.deleted;
      if (activeTab === 'deleted') return logo.deleted;
      return true;
    });
    
    relevantLogos.forEach(logo => {
      logo.tags.forEach(tag => {
        if (tag.trim()) {
          tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
        }
      });
    });
    
    return Array.from(tagCounts.entries())
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count);
  };

  // Get popular tags (top 10)
  const getPopularTags = () => {
    return getAllTagsWithCounts().slice(0, 10);
  };

  // Handle popular tag click
  const handlePopularTagClick = (tag: string) => {
    if (!activeFilters.includes(tag)) {
      setActiveFilters(prev => [...prev, tag]);
    }
  };

  // Remove active filter
  const removeFilter = (filterToRemove: string) => {
    setActiveFilters(prev => prev.filter(filter => filter !== filterToRemove));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setActiveFilters([]);
    setSearchTerm('');
  };

  const loadLogos = async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setLogos([]);
        setLastDoc(undefined);
        setHasMore(true);
      } else {
        setLoadingMore(true);
      }

      const result = await logoService.getLogos({
        lastDoc: reset ? undefined : lastDoc,
        searchTerm: debouncedSearchTerm,
        selectedTags: activeFilters,
        tab: activeTab
      });

      if (reset) {
        setLogos(result.logos);
      } else {
        setLogos(prev => [...prev, ...result.logos]);
      }

      setHasMore(result.hasMore);
      setLastDoc(result.lastDoc);
    } catch (error) {
      console.error('Failed to load logos:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadTabCounts = async () => {
    try {
      const counts = await logoService.getTabCounts();
      setTabCounts(counts);
    } catch (error) {
      console.error('Failed to load tab counts:', error);
    }
  };

  const loadAllTags = async () => {
    try {
      const tags = await logoService.getAllTags();
      setAllTags(tags);
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  const handleClaim = async (logoId: string) => {
    if (!user) return;
    
    try {
      await logoService.claimLogo(logoId, user.email!);
      await loadLogos(true);
      await loadTabCounts();
    } catch (error) {
      console.error('Failed to claim logo:', error);
    }
  };

  const handleRelease = async (logoId: string) => {
    try {
      await logoService.releaseLogo(logoId);
      await loadLogos(true);
      await loadTabCounts();
    } catch (error) {
      console.error('Failed to release logo:', error);
    }
  };

  const handleEdit = (logo: Logo) => {
    setEditingLogo(logo);
    setShowEditModal(true);
  };

  const handleAddSampleData = async () => {
    try {
      await addSampleLogos();
      await loadLogos(true);
      await loadTabCounts();
      await loadAllTags();
    } catch (error) {
      console.error('Failed to add sample data:', error);
    }
  };

  const handleDownload = (logo: Logo) => {
    const link = document.createElement('a');
    link.href = logo.fileUrl;
    link.download = `${logo.name.replace(/[^a-z0-9\s]/gi, '_').trim()}.svg`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDelete = async (logoId: string) => {
    if (!user) return;
    
    if (window.confirm('Are you sure you want to delete this logo? It will be moved to the Deleted tab and can be restored later.')) {
      try {
        await logoService.deleteLogo(logoId, user.email!);
        await loadLogos(true);
        await loadTabCounts();
      } catch (error) {
        console.error('Failed to delete logo:', error);
      }
    }
  };

  const handleRestore = async (logoId: string) => {
    try {
      await logoService.restoreLogo(logoId);
      await loadLogos(true);
      await loadTabCounts();
    } catch (error) {
      console.error('Failed to restore logo:', error);
    }
  };

  const handlePermanentDelete = async (logoId: string) => {
    if (!user) return;
    
    if (window.confirm('Are you sure you want to permanently delete this logo? This action cannot be undone.')) {
      try {
        await logoService.permanentlyDeleteLogo(logoId);
        await loadLogos(true);
        await loadTabCounts();
      } catch (error) {
        console.error('Failed to permanently delete logo:', error);
      }
    }
  };

  const handleUploadSuccess = () => {
    loadLogos(true);
    loadTabCounts();
    loadAllTags();
  };

  const handleEditSuccess = () => {
    loadLogos(true);
    loadAllTags();
  };

  // Permission check helpers
  const canUserClaim = (logo: Logo) => {
    return canClaimContent(logo.claimedBy || '');
  };

  const canUserDelete = (logo: Logo) => {
    return canDeleteContent(logo.createdBy);
  };

  const canUserEdit = (logo: Logo) => {
    return user?.email === logo.createdBy || permissions.canManageContent;
  };

  const canUserPermanentDelete = () => {
    return permissions.canManageContent;
  };

  const canUserRestore = () => {
    return permissions.canUploadContent; // Only users who can upload can restore
  };

  // Filter logos based on search term and active filters
  const filteredLogos = logos.filter(logo => {
    // Apply active filters with AND logic
    const passesActiveFilters = activeFilters.every(filter => {
      const filterLower = filter.toLowerCase();
      
      // Check basic info
      const basicMatch = [
        logo.name,
        logo.project,
        logo.notes,
        logo.createdBy
      ].some(field => field.toLowerCase().includes(filterLower));
      
      // Check tags
      const tagMatch = logo.tags.some(tag => tag.toLowerCase().includes(filterLower));
      
      return basicMatch || tagMatch;
    });
    
    if (!passesActiveFilters) return false;
    
    // Apply current search term if present
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Search in basic info
    const basicMatch = [
      logo.name,
      logo.project,
      logo.notes,
      logo.createdBy
    ].some(field => field.toLowerCase().includes(searchLower));
    
    // Search in tags
    const tagMatch = logo.tags.some(tag => tag.toLowerCase().includes(searchLower));
    
    return basicMatch || tagMatch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin" size={32} />
        <span className="ml-2">Loading logos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with upload button */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Image size={32} className="text-primary" />
          <div>
            <h2 className="text-2xl font-bold">Logo Library</h2>
            <p className="text-muted-foreground">{logos.length} logos available</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {logos.length === 0 && tabCounts.available === 0 && (
            <button 
              onClick={handleAddSampleData}
              className="bg-secondary text-secondary-foreground px-4 py-2 rounded-lg flex items-center gap-2"
            >
              Add Sample Data
            </button>
          )}
          {permissions.canUploadContent && (
            <button 
              onClick={() => setShowUploadModal(true)}
              className="bg-primary text-primary-foreground px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Plus size={20} />
              Upload Logo
            </button>
          )}
        </div>
      </div>

      {/* Search Section */}
      <div className="space-y-4">
        {/* Main Search Input */}
        <div className="relative max-w-2xl">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
          <input
            type="text"
            placeholder="Search logos by name, project, tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10 py-3 border rounded-lg w-full text-base"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground font-medium">Active filters:</span>
              <div className="flex flex-wrap gap-2">
                {activeFilters.map((filter) => (
                  <span
                    key={filter}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full border border-blue-200"
                  >
                    {filter}
                    <button
                      onClick={() => removeFilter(filter)}
                      className="hover:text-blue-600"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                >
                  Clear all
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Popular Tags */}
        {!searchTerm && activeFilters.length === 0 && logos.length > 0 && (
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">Popular tags:</p>
            <div className="flex flex-wrap gap-2">
              {(showAllTags ? getAllTagsWithCounts() : getPopularTags()).map(({ tag, count }) => (
                <button
                  key={tag}
                  onClick={() => handlePopularTagClick(tag)}
                  className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm hover:bg-blue-100 transition-colors flex items-center gap-1"
                >
                  {tag}
                  <span className="text-xs text-blue-500">({count})</span>
                </button>
              ))}
              {!showAllTags && getAllTagsWithCounts().length > 10 && (
                <button
                  onClick={() => setShowAllTags(true)}
                  className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm hover:bg-blue-100 transition-colors"
                >
                  See all ({getAllTagsWithCounts().length})
                </button>
              )}
              {showAllTags && (
                <button
                  onClick={() => setShowAllTags(false)}
                  className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm hover:bg-blue-100 transition-colors"
                >
                  Show less
                </button>
              )}
            </div>
          </div>
        )}

        {/* Search Results Summary */}
        {(searchTerm || activeFilters.length > 0) && (
          <div className="text-sm text-muted-foreground">
            Found {filteredLogos.length} {filteredLogos.length === 1 ? 'logo' : 'logos'}
            {activeFilters.length > 0 && ` matching ${activeFilters.length} filter${activeFilters.length === 1 ? '' : 's'}`}
            {searchTerm && ` and search term "${searchTerm}"`}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('available')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'available'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Available ({tabCounts.available})
          </button>
          <button
            onClick={() => setActiveTab('claimed')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'claimed'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Claimed ({tabCounts.claimed})
          </button>
          <button
            onClick={() => setActiveTab('deleted')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'deleted'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Deleted ({tabCounts.deleted})
          </button>
        </nav>
      </div>

      {/* Empty state */}
      {filteredLogos.length === 0 && !loading && (
        <div className="text-center py-12">
          {logos.length === 0 ? (
            <p className="text-muted-foreground">
              {tabCounts.available === 0 && tabCounts.claimed === 0 && tabCounts.deleted === 0
                ? "No logos uploaded yet. Click 'Add Sample Data' to get started!"
                : `No logos found in ${activeTab} category.`
              }
            </p>
          ) : (
            <div>
              <p className="text-muted-foreground">No logos found matching your filters.</p>
              {(activeFilters.length > 0 || searchTerm) && (
                <button
                  onClick={clearAllFilters}
                  className="text-primary hover:underline mt-2"
                >
                  Clear all filters to see all logos
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Logo Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {filteredLogos.map((logo) => (
          <div key={logo.id} className="bg-white border rounded-lg p-4 space-y-3">
            <div className="aspect-square bg-muted rounded-lg flex items-center justify-center overflow-hidden p-4">
              {logo.fileUrl ? (
                <img 
                  src={logo.fileUrl} 
                  alt={logo.name}
                  className="max-w-full max-h-full object-contain"
                  style={{ maxWidth: '120px', maxHeight: '120px' }}
                  loading="lazy"
                />
              ) : (
                <div className="text-muted-foreground text-sm">No image</div>
              )}
            </div>
            
            <div className="space-y-2">
              <h3 className="font-semibold text-sm">{logo.name}</h3>
              <p className="text-xs text-muted-foreground">{logo.project}</p>
              
              {/* Tags - Same style as People module popular skills */}
              <div className="flex flex-wrap gap-1">
                {logo.tags.map((tag) => (
                  <span 
                    key={tag} 
                    className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs hover:bg-blue-100 transition-colors"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              
              {logo.notes && (
                <p className="text-xs text-muted-foreground italic">{logo.notes}</p>
              )}
              
              <p className="text-xs text-muted-foreground">
                Uploaded by: <span className="font-medium">{logo.createdBy}</span>
              </p>
              
              {logo.claimed && !logo.deleted && (
                <p className="text-xs text-orange-600 font-medium">
                  Claimed by: {logo.claimedBy}
                </p>
              )}
              
              {logo.deleted && (
                <p className="text-xs text-red-600 font-medium">
                  Deleted by: {logo.deletedBy}
                </p>
              )}
            </div>

            <div className="space-y-2">
              {!logo.deleted ? (
                <>
                  {/* Primary Actions - Clean button style like People module */}
                  <div className="flex gap-2">
                    {canUserClaim(logo) && (
                      <button 
                        onClick={() => logo.claimed ? handleRelease(logo.id) : handleClaim(logo.id)}
                        className="flex-1 text-foreground py-2 rounded-lg flex items-center justify-center gap-1 hover:bg-primary/10 hover:text-primary transition-colors border border-gray-200 hover:border-primary/40"
                      >
                        <Hand size={14} />
                        {logo.claimed ? 'Release' : 'Claim'}
                      </button>
                    )}
                    
                    <button 
                      onClick={() => handleDownload(logo)}
                      className="flex-1 text-foreground py-2 rounded-lg flex items-center justify-center gap-1 hover:bg-primary/10 hover:text-primary transition-colors border border-gray-200 hover:border-primary/40"
                    >
                      <Download size={14} />
                      Download
                    </button>
                  </div>
                  
                  {/* Secondary Actions - Clean button style like People module */}
                  {(canUserEdit(logo) || canUserDelete(logo)) && (
                    <div className="flex gap-1">
                      {canUserEdit(logo) && (
                        <button 
                          onClick={() => handleEdit(logo)}
                          className="flex-1 py-1 text-foreground rounded-lg flex items-center justify-center gap-1 hover:bg-purple-100 hover:text-purple-600 transition-colors border border-gray-200 hover:border-purple-200 text-xs"
                        >
                          <Edit size={12} />
                          Edit
                        </button>
                      )}
                      
                      {canUserDelete(logo) && (
                        <button 
                          onClick={() => handleDelete(logo.id)}
                          className="flex-1 py-1 text-foreground rounded-lg flex items-center justify-center gap-1 hover:bg-red-100 hover:text-red-600 transition-colors border border-gray-200 hover:border-red-200 text-xs"
                        >
                          <Trash2 size={12} />
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Deleted state actions - Clean button style */}
                  <div className="flex gap-2">
                    {canUserRestore() && (
                      <button 
                        onClick={() => handleRestore(logo.id)}
                        className="flex-1 py-2 text-foreground rounded-lg flex items-center justify-center gap-1 hover:bg-green-100 hover:text-green-700 transition-colors border border-gray-200 hover:border-green-200"
                      >
                        <RotateCcw size={14} />
                        Restore
                      </button>
                    )}
                    
                    {canUserPermanentDelete() && (
                      <button 
                        onClick={() => handlePermanentDelete(logo.id)}
                        className="py-2 px-3 text-foreground rounded-lg flex items-center gap-1 hover:bg-red-100 hover:text-red-700 transition-colors border border-gray-200 hover:border-red-200 text-xs"
                        title="Permanently delete (Admin/Owner only)"
                      >
                        <AlertTriangle size={12} />
                        Permanent
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Load More Button */}
      {hasMore && filteredLogos.length > 0 && (
        <div className="flex justify-center">
          <button
            onClick={() => loadLogos(false)}
            disabled={loadingMore}
            className="bg-secondary text-secondary-foreground px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-secondary/80 disabled:opacity-50"
          >
            {loadingMore ? (
              <>
                <Loader className="animate-spin" size={16} />
                Loading more...
              </>
            ) : (
              'Load More Logos'
            )}
          </button>
        </div>
      )}

      {/* Upload Modal */}
      {permissions.canUploadContent && (
        <UploadLogoModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
          existingTags={allTags}
        />
      )}

      {/* Edit Modal */}
      <EditLogoModal
        isOpen={showEditModal}
        logo={editingLogo}
        existingTags={allTags}
        onClose={() => {
          setShowEditModal(false);
          setEditingLogo(null);
        }}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};